import { Trans } from "@lingui/macro";
import LanguageMenu from "src/atoms/LanguageMenu/LanguageMenuNew";
import { menuOptions } from "src/atoms/LanguageMenu/LanguageMenuOptions";
import { Menu as MenuAtom } from "src/atoms/Menu";
import { CloseSmall } from "src/icons/CloseSmall";
import { Locale } from "src/lib/Intl";
import {
  ListTitle,
  LocaleName,
  Menu,
  MenuMobile,
  MenuWrapper,
} from "./LanguageSwitcher.styled";
import { CloseButton } from "src/atoms/CloseButton";
import { useLingui } from "@lingui/react";
import { translatedLanguageMap } from "src/atoms/LanguageMenu/getTranslatedMenuOptions";
import { DesktopMenuList } from "./components/DesktopMenuList";
import { Toggle } from "./components/Toggle";
import { MobileMenuList } from "./components/MobileMenuList";
import { MenuList } from "./components/MenuList";

type LanguageSwitcherProps = {
  svgPrefixId: string;
  locale: Locale;
  isFooter?: boolean;
  showRegion?: boolean;
  showLanguage?: boolean;
  showCurrency?: boolean;
};

function LanguageSwitcher({
  svgPrefixId,
  locale,
  isFooter = false,
  showRegion = true,
  showLanguage = true,
  showCurrency = true,
}: LanguageSwitcherProps) {
  const currentLocale = menuOptions.find((option) => option.id === locale);
  const language = currentLocale?.language;
  const currency = currentLocale?.currency;
  const { _ } = useLingui();
  const translatedLanguage = _(
    translatedLanguageMap[currentLocale?.id || "en"]
  );

  const footerClass = isFooter ? "footer" : "";

  return (
    <MenuWrapper className={footerClass}>
      <Menu>
        <p className="sr-only" id="domain-switcher-label">
          Country switcher
        </p>
        <p className="sr-only" id="domain-switcher-description">
          Selected shop is ${language}. Current currency is ${currency}. To open
          this listbox press Enter. To leave the opened listbox press Escape.
        </p>
        <MenuAtom.Button>
          {({ isOpen, onToggle }) => (
            <Toggle
              isOpen={isOpen}
              onToggle={onToggle}
              isFooter={isFooter}
              svgPrefixId="menu-toggle"
              aria-expanded={isOpen}
              aria-labelledby="domain-switcher-label"
              aria-describedby="domain-switcher-description"
            />
          )}
        </MenuAtom.Button>
        <DesktopMenuList
          isFooter={isFooter}
          svgPrefixId={svgPrefixId}
          showRegion={showRegion}
          showLanguage={showLanguage}
          showCurrency={showCurrency}
        />
      </Menu>
      <MenuMobile>
        <MenuAtom.Button>
          {({ isOpen, onToggle }) => (
            <Toggle
              isOpen={isOpen}
              onToggle={onToggle}
              isFooter={isFooter}
              svgPrefixId="menu-mobile-toggle"
            >
              <LocaleName className={footerClass}>
                {translatedLanguage}
              </LocaleName>
            </Toggle>
          )}
        </MenuAtom.Button>
        <MenuList>
          {({ onToggle, isOpen }) => (
            <MobileMenuList isOpen={!!isOpen} isFooter={isFooter}>
              <CloseButton
                onClick={onToggle}
                icon={<CloseSmall />}
                sx={{
                  width: 48,
                  height: 48,
                  margin: "8px 8px 8px auto",
                  justifyContent: "center",
                }}
              />
              <ListTitle as="h4" mx={5}>
                <Trans>Choose your language</Trans>
              </ListTitle>
              <LanguageMenu
                svgPrefixId={svgPrefixId}
                showRegion={showRegion}
                showLanguage={showLanguage}
                showCurrency={showCurrency}
              />
            </MobileMenuList>
          )}
        </MenuList>
      </MenuMobile>
    </MenuWrapper>
  );
}

export { LanguageSwitcher };
